// Classification de texte libre vers une option de la question courante.
//
// Stratégie offline : matching de mots-clés (Jaccard pondéré) pour chaque
// option. Si aucun match dépasse un seuil, on retourne `null` → le moteur
// applique des poids neutres (la jauge ne bouge pas), et le texte est tout
// de même enregistré dans la session pour analyse offline.
//
// Stratégie online (si apiUrl) : on envoie le texte + la question au
// backend qui peut faire un appel LLM plus précis. Le widget tente
// d'abord la classification locale, puis confirme par le backend.

import type { Question, QuestionOption } from './types';

export interface ClassifyResult {
  /** Option choisie, ou null si aucune assez confiante. */
  option: QuestionOption | null;
  /** Score de confiance dans [0,1]. */
  confidence: number;
  /** Méthode utilisée pour la classification (debug). */
  method: 'keyword' | 'fallback' | 'dont_know';
  /** Vrai si l'utilisatrice a exprimé "je ne sais pas" / "aucune idée" / ... */
  isDontKnow?: boolean;
}

/**
 * Détecte les formulations de "je ne sais pas" indépendamment de la question.
 * Couvre les variantes courantes (français + un peu d'anglais),
 * insensible aux accents et à la ponctuation.
 */
export function isDontKnowPattern(text: string): boolean {
  const t = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ') // apostrophes → espace, pour que "j'en" devienne "j en"
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return false;
  return DONT_KNOW_PATTERNS.some((re) => re.test(t));
}

const DONT_KNOW_PATTERNS: RegExp[] = [
  /\bje\s*(ne)?\s*sais\s*pas\b/, // "je ne sais pas", "je sais pas"
  /\bj\s*sais\s*pas\b/,           // "j'sais pas"
  /\bsais\s*pas\b/,               // "sais pas"
  /\baucune?\s*idee\b/,           // "aucune idée"
  /\bj\s*en\s*sais\s*rien\b/,     // "j'en sais rien"
  /\bj\s*en\s*ai\s*aucune\b/,     // "j'en ai aucune idée"
  /\bpas\s*sure?\b/,              // "pas sûre", "pas sur"
  /\bincertaine?\b/,              // "incertaine"
  /\bsais\s*plus\b/,              // "j'sais plus"
  /\bno\s*idea\b/,                // "no idea"
  /\bdunno\b/,                    // "dunno"
  /^\s*sp\s*$/,                   // "sp" (abrégé fréquent)
  /^\s*je\s*sais\s*pas\s*$/,      // version compacte
];

const STOPWORDS = new Set([
  'je', 'tu', 'il', 'elle', 'on', 'nous', 'vous', 'ils', 'elles',
  'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'au', 'aux',
  'mon', 'ma', 'mes', 'ton', 'ta', 'tes', 'son', 'sa', 'ses',
  'et', 'ou', 'mais', 'donc', 'car', 'ni', 'or', 'que', 'qui',
  'est', 'sont', 'ai', 'as', 'a', 'avons', 'avez', 'ont',
  'plus', 'moins', 'tres', 'très', 'assez', 'peu', 'beaucoup',
  'pas', 'ne', 'pour', 'avec', 'sans', 'dans', 'sur', 'sous',
  'à', 'aÌ', 'a', 'd', 'l', 's', 'n', 'm', 't', 'c', 'j',
  'me', 'te', 'se', 'lui', 'leur', 'y', 'en', 'ce', 'cet', 'cette',
  'mon', 'ce', 'fait', 'fais', 'faire', 'sais',
]);

/**
 * Classifie un texte libre vers la meilleure option d'une question.
 *
 * Stratégie : compte le nombre de tokens utilisateur qui matchent les mots-clés
 * de chaque option. Le gagnant = option avec le plus de matchs, à condition
 * d'avoir au moins un match ET d'être strictement devant le runner-up (sinon
 * c'est ambigu, on retourne null pour ne pas biaiser la jauge).
 */
export function classifyFreetext(text: string, question: Question): ClassifyResult {
  // Priorité absolue : "je ne sais pas" → skip universel.
  if (isDontKnowPattern(text)) {
    return { option: null, confidence: 1, method: 'dont_know', isDontKnow: true };
  }

  const tokens = tokenize(text);
  if (tokens.size === 0) {
    return { option: null, confidence: 0, method: 'fallback' };
  }

  let best: { opt: QuestionOption; score: number } | null = null;
  let runnerUp = 0;
  for (const opt of question.options) {
    const score = scoreOption(tokens, opt);
    if (best === null || score > best.score) {
      runnerUp = best?.score ?? 0;
      best = { opt, score };
    } else if (score > runnerUp) {
      runnerUp = score;
    }
  }

  if (!best || best.score === 0) {
    return { option: null, confidence: 0, method: 'keyword' };
  }
  // Ambiguïté : deux options à égalité → on n'inflige pas un choix arbitraire.
  if (best.score === runnerUp) {
    return { option: null, confidence: 0, method: 'keyword' };
  }
  const confidence = Math.min(best.score / Math.max(tokens.size, 1), 1);
  return { option: best.opt, confidence, method: 'keyword' };
}

function tokenize(text: string): Set<string> {
  const norm = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // retire les accents
    .replace(/[^a-z0-9\s]/g, ' ');
  const out = new Set<string>();
  for (const w of norm.split(/\s+/)) {
    if (w.length < 3) continue;
    if (STOPWORDS.has(w)) continue;
    out.add(stem(w));
  }
  return out;
}

/**
 * Stemming itératif : on enlève les suffixes courants un par un jusqu'à
 * stabilité, en commençant par les plus courts (s, e). Sans descendre
 * sous 4 caractères (évite "très" → "tr").
 *
 * Cet ordre garantit que "hanches" passe par "hanche" puis "hanch", et que
 * "hanche" passe directement par "hanch" — donc même radical, ce qui est
 * indispensable pour matcher singulier et pluriel.
 */
export function stem(w: string): string {
  // ORDRE IMPORTANT : suffixes simples d'abord (s, e), puis composés.
  const SUFFIXES = /(s|e|aux|ous|ses?|ales?|eures?|euses?|sions?|tions?|ements?)$/;
  let prev: string;
  let cur = w;
  do {
    prev = cur;
    const next = cur.replace(SUFFIXES, '');
    if (next.length < 4) break;
    cur = next;
  } while (cur !== prev);
  return cur;
}

function scoreOption(tokens: Set<string>, opt: QuestionOption): number {
  // IMPORTANT : on n'utilise QUE les keywords explicites quand ils sont fournis.
  // Les labels seraient ambigus : "Mes hanches sont plus larges que mes épaules"
  // contient à la fois 'hanches' et 'épaules', ce qui ferait matcher cette
  // option avec n'importe quelle réponse mentionnant l'une OU l'autre.
  const optWords = new Set<string>();
  if (opt.keywords && opt.keywords.length > 0) {
    for (const kw of opt.keywords) optWords.add(stem(kw.toLowerCase()));
  } else {
    // Fallback pour les options sans keywords explicites (ex: sizing XS/S/M).
    for (const w of tokenize(opt.label)) optWords.add(w);
  }
  if (optWords.size === 0) return 0;

  let matches = 0;
  for (const t of tokens) if (optWords.has(t)) matches++;
  return matches;
}
